import { Injectable } from '@angular/core';
import { MemberStore } from './member.store';
import { tap, mapTo } from 'rxjs/operators';
import { ApiService } from '../../shared/services/core/api.service';
import { MemberDetails } from './models/member-details.model';
import { UserContext } from './models/user-context.model';
import { Observable, firstValueFrom, of } from 'rxjs';
import { MemberQuery } from './member.query';
import { LoggingService } from '../../shared/services/core/logging.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ProviderName } from '../models/login-response.model';
import { Representative } from './models/representative.model';
import { config } from '../../shared/config';

@Injectable({ providedIn: 'root' })
export class MemberService {
  constructor(
    private memberStore: MemberStore,
    private memberQuery: MemberQuery,
    private api: ApiService,
    private jwtHelper: JwtHelperService,
    private logService: LoggingService
  ) { }

  /** Fetches the member details and puts them in the store. */
  fetchMemberDetails(): Observable<null> {
    this.memberStore.setLoading(true);
    let user = this.memberQuery.getValue().user;

    // if no user is returned from the store, get memberId from the token
    if (!user) {
      let accessToken = localStorage.getItem('token');
      let refreshToken = localStorage.getItem('refresh');
      if (accessToken && refreshToken) {
        if (!this.jwtHelper.isTokenExpired(refreshToken)) {
          let decodedToken = this.jwtHelper.decodeToken(accessToken);
          user = new UserContext();
          user.memberId = decodedToken.member_id;
        }
      } else {
        this.logService.warning('Failed to get member details as the user is not logged in.');
        return of(null);
      }
    }

    const request$: Observable<null> = this.api.getById<MemberDetails>('members', user!.memberId).pipe(
      tap((details) => this.memberStore.set([details])),
      mapTo(null)
    );

    return this.memberQuery.getHasCache() ? of(null) : request$;
  }

  /** Update member details */
  updateMemberDetails(portalId: string, memberDetails: MemberDetails) {
    this.api.put('members', portalId, memberDetails).toPromise();
    this.memberStore.set([memberDetails]);
  }

  /** Update member and return api request observable */
  updateMemberDetailsAsync(portalId: string, memberDetails: MemberDetails): Observable<MemberDetails> {
    return this.api.put<MemberDetails>('members', portalId, memberDetails);
  }

  /** Update the user context in the store. */
  setUserContext(user: UserContext) {
    this.logService.debug('Setting user context', user);
    this.memberStore.update({ user });
  }

  setPopulation(populationId: number) {
    this.memberStore.update((state) => ({ ...state, user: { ...state.user, populationId } }));
  }

  setLcid(lcid: number): Promise<any> {
    const promise = firstValueFrom(this.api.post('members/changelocale/' + lcid, undefined));

    promise.then(() => {
      localStorage.setItem('lcid', lcid.toString());
      this.memberStore.update((state) => ({ ...state, user: { ...state.user, lcid: lcid.toString() } }));
    });

    return promise;
  }

  /** Update the available accounts for the user to choose from in the store. */
  setAvailableAccounts(availableAccounts: MemberDetails[]) {
    this.logService.debug('Settings available accounts', availableAccounts);
    this.memberStore.update({ availableAccounts });
  }

  resetPassword(email: string, clubId: number) {
    let lcid = localStorage.getItem('lcid') ?? config.defaultLcid;
    const resetFormBody = {
      Username: email,
      Lcid: lcid,
      ClubId: clubId,
    };
    return this.api.post<Object>(`members/resetpassword?providerName=${ProviderName.Fortress}`, resetFormBody).toPromise();
  }

  changePassword(resetKey: string, unhashedPassword: string, passwordConfirmation: string) {
    return this.api
      .post<any>(
        `members/resetpasswordbyresetkey?resetKey=${resetKey}&unhashedPassword=${unhashedPassword}&passwordConfirmation=${passwordConfirmation}`,
        {}
      )
      .toPromise();
  }

  getRepresentative() {
    return this.api.getAnySingle<Representative>('members/rep');
  }

  getRepresentativeImage(repId: number): Observable<Blob | undefined> {
    return this.api.getFile(`content/getrepimage?repid=${repId}`)
  }
}
